<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1400px"
    persistent
    scrollable
    transition="dialog-bottom-transition"
  >
    <v-card>
      <v-form>
        <v-card-title>
          <span class="headline"
            >Órdenes Asignadas - {{ operator?.user_name }}</span
          >
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row align="center" class="mb-3">
            <v-col cols="auto">
              <v-chip color="primary" variant="tonal">
                <v-icon start>mdi-package-variant</v-icon>
                {{ shipment_orders.length }} órdenes
              </v-chip>
            </v-col>
            <v-col cols="auto" v-if="selected_orders.length > 0">
              <v-btn
                color="error"
                variant="outlined"
                size="small"
                prepend-icon="mdi-close-circle"
                @click="openBulkUnassignDialog"
              >
                Desasignar seleccionadas
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="4">
              <v-text-field
                v-model="search"
                append-inner-icon="mdi-magnify"
                single-line
                hide-details
                label="Buscar"
                density="compact"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headers"
            :items="shipment_orders"
            :search="search"
            :loading="loading"
            v-model="selected_orders"
            :show-select="false"
            item-value="id"
            loading-text="Cargando..."
          >
            <template v-slot:item="{ item, isSelected, toggleSelect }">
              <tr>
                <td>
                  <v-btn
                    variant="text"
                    size="small"
                    @click="toggleExpand(item)"
                    icon
                  >
                    <v-icon>{{
                      isExpanded(item)
                        ? "mdi-chevron-down"
                        : "mdi-chevron-right"
                    }}</v-icon>
                  </v-btn>
                </td>
                <td class="pl-2">
                  <v-checkbox
                    style="margin: 0px; padding: 0px"
                    hide-details
                    :model-value="isSelected({ value: item.id })"
                    @update:modelValue="toggleSelect({ value: item.id })"
                  />
                </td>
                <td>
                  <div
                    class="font-weight-bold"
                    :style="
                      item.wave_id != null
                        ? 'margin-top: 5px; margin-bottom: -5px'
                        : ''
                    "
                  >
                    {{ item.code }}
                  </div>
                  <span class="text-caption text-grey-600" v-if="item.wave_id"
                    >Ola {{ item.wave_id }}</span
                  >
                </td>
                <td>{{ item.client_user_name }}</td>
                <td>
                  {{ item.date.split(" ")[0] }}<br />
                  <span class="text-caption text-grey-600">{{
                    item.date.split(" ")[1] || ""
                  }}</span>
                </td>
                <td>
                  {{ item.dock_name != null ? item.dock_name : "-" }}<br />
                  <span class="text-caption text-grey-600">{{
                    item.dock_almacen_code != null ? item.dock_almacen_code : ""
                  }}</span>
                </td>
                <td>
                  {{
                    item.canal_venta_name != null ? item.canal_venta_name : "-"
                  }}
                </td>
                <td>
                  {{ item.document_type_name }}<br />
                  <span
                    v-if="item.external_reference != null"
                    class="text-caption text-grey-600"
                  >
                    <span class="table-text-secondary">Ref:</span>
                    {{ item.external_reference }}
                  </span>
                </td>
                <td>
                  {{ item.address }}
                  <p class="text-caption text-grey-600 mb-0">
                    {{ item.location }}
                  </p>
                </td>
                <td class="text-center">
                  <div class="d-flex flex-column align-center gap-1 mt-2">
                    <v-progress-circular
                      :model-value="getTaskProgress(item)"
                      :color="getProgressColor(item)"
                      :size="45"
                      :width="5"
                    >
                      <span class="text-caption font-weight-bold">{{ getTaskProgress(item) }}%</span>
                    </v-progress-circular>
                    <span class="text-caption text-grey-600">
                      {{ item.completed_tasks || 0 }}/{{ item.total_tasks || 0 }}
                    </span>
                  </div>
                </td>
                <td>
                  <div
                    class="status-badge font-weight-medium"
                    :class="'status-envio-' + item.status"
                  >
                    {{ shipmentOrderService.getStatusName(item.status) }}
                    <p
                      class="text-caption text-grey-600 mb-0"
                      v-if="item.sub_status_name"
                    >
                      {{ shipmentOrderService.getSubStatusName(item.sub_status) }}
                    </p>
                  </div>
                </td>
                <td class="text-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        small
                        color="red"
                        @click="openUnassignDialog(item)"
                        size="small"
                      >
                        mdi-close-circle
                      </v-icon>
                    </template>
                    <span>Desasignar orden</span>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="isExpanded(item)" :key="`expanded-${item.id}`">
                <td :colspan="headers.length" class="pa-0">
                  <div class="pa-4 bg-grey-lighten-4">
                    <h6 class="mb-3">Tareas de Preparación</h6>
                    
                    <!-- Loading state -->
                    <div v-if="order_tasks[item.id]?.loading" class="text-center py-4">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      <p class="mt-2 text-grey">Cargando tareas...</p>
                    </div>

                    <!-- Tabla de tareas -->
                    <v-table v-else-if="order_tasks[item.id]?.tasks?.length > 0" density="compact" class="tasks-table">
                      <thead>
                        <tr>
                          <th class="text-left">Nº</th>
                          <th class="text-left">Producto</th>
                          <th class="text-left">LPN Reservado</th>
                          <th class="text-left">LPN Origen/Destino</th>
                          <th class="text-left">Cantidades</th>
                          <th class="text-left">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="task in order_tasks[item.id].tasks" :key="task.pd_id">
                          <td class="text-caption">#{{ task.pd_id }}</td>
                          <td>
                            <div class="font-weight-medium">{{ task.product_name }}</div>
                            <span class="text-caption text-grey-600">SKU: {{ task.product_sku }}</span>
                          </td>
                          <td>{{ task.lpn_code || '-' }}</td>
                          <td>
                            <div class="d-flex align-center gap-3 mt-1 mb-1">
                              <div v-if="task.lpn_origen_code || task.ubicacion_origen_code" class="d-flex flex-column">
                                <span class="font-weight-bold text-caption">Origen</span>
                                <span>{{ task.lpn_origen_code || "" }}</span>
                                <span class="text-caption text-grey-600 nowrap">{{ task.ubicacion_origen_code || "" }}</span>
                              </div>

                              <v-divider v-if="(task.lpn_origen_code || task.ubicacion_origen_code) && (task.lpn_destino_code || task.ubicacion_destino_code)" vertical class="mx-2"></v-divider>

                              <div v-if="task.lpn_destino_code || task.ubicacion_destino_code" class="d-flex flex-column">
                                <span class="font-weight-bold text-caption">Destino</span>
                                <span>{{ task.lpn_destino_code || "" }}</span>
                                <span class="text-caption text-grey-600 nowrap">{{ task.ubicacion_destino_code || "" }}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div class="stock-row">
                              <span class="stock-label">A preparar:</span>
                              <span class="stock-value">{{ task.pd_amount_to_pick }}</span>
                            </div>
                            <div class="stock-row">
                              <span class="stock-label text-grey-600">Preparada:</span>
                              <span class="stock-value text-grey-600">{{ task.pt_amount_picked || "0" }}</span>
                            </div>
                          </td>
                          <td>
                             <span class="status-badge" :class="'status-' + task.pd_status">
                                {{ pickingDetailService.getStatusName(task.pd_status) }}
                              </span>
                          </td>
                        </tr>
                      </tbody>
                    </v-table>

                    <!-- Sin tareas -->
                    <div v-else class="text-center py-4 text-grey">
                      <v-icon size="48" color="grey-lighten-1">mdi-clipboard-text-off</v-icon>
                      <p class="mt-2">No hay tareas asignadas para esta orden</p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="close">Cerrar</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>

    <!-- Modal Confirmar Desasignación -->
    <DialogYesNo
      :open="dialog_unassign"
      title="Desasignar Orden"
      :message="unassign_message"
      :loading="loading_unassign"
      @accept="confirmUnassign"
      @cancel="closeUnassignDialog"
    />
  </v-dialog>
</template>

<script>
import { shipmentOrderService } from "@/libs/ws/shipmentOrderService";
import { pickingDetailService } from "@/libs/ws/pickingDetailService";
import DialogYesNo from "@/components/DialogYesNo.vue";

export default {
  components: {
    DialogYesNo,
  },
  props: {
    modelValue: Boolean,
    operator: Object,
    dateFrom: String,
    dateTo: String,
  },
  data() {
    return {
      loading: false,
      shipmentOrderService: shipmentOrderService,
      pickingDetailService:pickingDetailService,
      search: "",
      shipment_orders: [],
      selected_orders: [],
      expanded: [],
      order_tasks: {}, // { order_id: { tasks: [], loading: false } }
      dialog_unassign: false,
      loading_unassign: false,
      selected_order: null,
      unassign_message: "",
      headers: [
        { text: "", value: "expand", sortable: false, width: "30px" },
        { text: "", value: "data-table-select", sortable: false, width: "40px" },
        {
          title: "Número",
          align: "left",
          sortable: true,
          key: "code",
        },
        {
          title: "Cliente",
          align: "left",
          sortable: true,
          key: "client_user_name",
        },
        {
          title: "Fecha",
          align: "left",
          sortable: true,
          key: "date",
        },
        {
          title: "Muelle",
          align: "left",
          sortable: true,
          key: "dock_name",
        },
        {
          title: "Canal",
          align: "left",
          sortable: true,
          key: "canal_venta_name",
        },
        {
          title: "Tipo Documento",
          align: "left",
          sortable: true,
          key: "document_type_name",
        },
        {
          title: "Dirección",
          align: "left",
          sortable: true,
          key: "address",
        },
        {
          title: "Progreso",
          align: "center",
          sortable: false,
          key: "progress",
          width: "120px",
        },
        {
          title: "Estado",
          align: "left",
          sortable: true,
          key: "status_name",
        },
        {
          title: "Acciones",
          align: "center",
          sortable: false,
          key: "actions",
          width: "100px",
        },
      ],
    };
  },
  watch: {
    modelValue: {
      handler(newValue) {
        if (newValue && this.operator) {
          this.loadOrders();
        }
      },
      immediate: true,
    },
  },
  methods: {
    loadOrders() {
      this.loading = true;
      this.shipment_orders = [];

      const params = {
        user: { id: this.operator.user_id },
        user_only_assigned: true,
        date_from: this.dateFrom,
        date_to: this.dateTo,
        date_type: "assigned_date",
        status_arr: [{ id: shipmentOrderService.STATUS_STOCK_RESERVADO }, { id: shipmentOrderService.STATUS_EN_PREPARACION }],
        include_picking_tasks_count: true
      };

      shipmentOrderService
        .filter(params)
        .then((response) => {
          this.shipment_orders = response.result.list || [];
        })
        .catch((e) => {
          console.error("Error cargando órdenes del operario:", e);
          this.$toast?.error("Error al cargar las órdenes del operario");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toggleExpand(item) {
      const index = this.expanded.indexOf(item.id);
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(item.id);
        // Cargar tareas si no están cargadas
        if (!this.order_tasks[item.id]) {
          this.loadTasksForOrder(item.id);
        }
      }
    },
    isExpanded(item) {
      return this.expanded.includes(item.id);
    },
    loadTasksForOrder(shipment_order_id) {
      // Inicializar estado de carga (Vue 3 compatible)
      this.order_tasks = {
        ...this.order_tasks,
        [shipment_order_id]: {
          tasks: [],
          loading: true
        }
      };

      const params = {
        shipment_order: { id: shipment_order_id },
        assigned_user: { id: this.operator.user_id },
        user_only_assigned: true
      };

      pickingDetailService
        .filter(params)
        .then((response) => {
          if (response.status) {
            this.order_tasks[shipment_order_id].tasks = response.result.list || [];
          } else {
            console.error("Error al cargar tareas:", response.msg);
            this.$toast?.error("Error al cargar las tareas");
          }
        })
        .catch((e) => {
          console.error("Error al cargar tareas:", e);
          this.$toast?.error("Error al cargar las tareas");
        })
        .finally(() => {
          this.order_tasks[shipment_order_id].loading = false;
        });
    },
    close() {
      this.expanded = [];
      this.search = "";
      this.selected_orders = [];
      this.order_tasks = {};
      this.$emit("update:modelValue", false);
    },
    openUnassignDialog(item) {
      this.selected_order = item;
      this.unassign_message = `¿Está seguro que desea desasignar la orden ${item.code} del operario ${this.operator.user_name}?`;
      this.dialog_unassign = true;
    },
    openBulkUnassignDialog() {
      const count = this.selected_orders.length;
      this.selected_order = null;
      this.unassign_message = `¿Está seguro que desea desasignar ${count} órdenes seleccionadas del operario ${this.operator.user_name}?`;
      this.dialog_unassign = true;
    },
    closeUnassignDialog() {
      this.dialog_unassign = false;
      this.selected_order = null;
      this.selected_orders = [];
      this.unassign_message = "";
    },
    confirmUnassign() {
      this.loading_unassign = true;

      // Si es una sola orden o múltiples
      const params = this.selected_order
        ? { shipment_order_id: this.selected_order.id }
        : { shipment_order_ids: this.selected_orders };

      shipmentOrderService
        .unassignPickingUser(params)
        .then((response) => {
          if (response.status) {
            const count = this.selected_order ? 1 : this.selected_orders.length;
            this.$toast?.success(
              count === 1
                ? `Orden desasignada correctamente`
                : `${count} órdenes desasignadas correctamente`
            );
            this.closeUnassignDialog();
            this.close();
            // Emitir evento para recargar datos en el componente padre
            this.$emit("order-unassigned");
          } else {
            this.$toast?.error(
              response.msg || "Error al desasignar la orden"
            );
          }
        })
        .catch((e) => {
          console.error("Error desasignando orden:", e);
          this.$toast?.error("Error al desasignar la orden del operario");
        })
        .finally(() => {
          this.loading_unassign = false;
        });
    },
    getTaskProgress(item) {
      if (!item.total_tasks || item.total_tasks === 0) return 0;
      return Math.round((item.completed_tasks / item.total_tasks) * 100);
    },
    getProgressColor(item) {
      const progress = this.getTaskProgress(item);
      if (progress === 100) return 'success';
      if (progress >= 50) return 'primary';
      if (progress > 0) return 'warning';
      return 'grey';
    },
  },
};
</script>

<style scoped>


/* Stock display */
.stock-row {
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.4;
}

.stock-label {
  font-size: 0.875rem;
}

.stock-value {
  font-weight: 600;
  font-size: 0.875rem;
}

.nowrap {
  white-space: nowrap;
}

.tasks-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.tasks-table thead {
  background-color: #f5f5f5;
}

.tasks-table th {
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  color: #616161 !important;
}

.tasks-table td {
  font-size: 0.875rem;
}

.tasks-table tbody tr:hover {
  background-color: #f9f9f9;
}
</style>
